import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { StatCard } from '../components/dashboard/StatCard';
import { RecentActivityList } from '../components/dashboard/RecentActivityList';
import { PendingPaymentTable } from '../components/dashboard/PendingPaymentTable';
import { MonthSelector } from '../components/dashboard/MonthSelector';
import { DashboardGreeting } from '../components/dashboard/DashboardGreeting';
import { searchPatients, getDashboardData, Patient, DashboardData } from '../services/api';
import { OnboardingTooltip, useOnboardingTooltip } from '../components/OnboardingTooltip';
import { currentMonthIn, currentMonthLocal } from '../utils/clinicTime';

// Initial value only. Once the dashboard responds with the clinic's timezone, "current month"
// is decided by the clinic's clock rather than this device's.
const getCurrentMonth = currentMonthLocal;

const formatCurrency = (amount: number) => {
  return `Q${amount.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// "2026-06-24" -> "24 de junio"
const formatDayMonth = (isoDate: string) => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('es-GT', {
    day: 'numeric',
    month: 'long',
  });
};

// Explains what the revenue trend percentage is measured against. While the month is still
// running the backend compares it to the same stretch of the previous month, so say so —
// otherwise the number reads as a full-month comparison and looks alarming.
const buildRevenueSubtitle = (revenue: DashboardData['monthly_revenue']) => {
  if (!revenue.has_comparison) {
    return 'Sin ingresos el mes anterior';
  }
  if (revenue.is_partial_month) {
    return `vs. ${formatCurrency(revenue.previous)} al ${formatDayMonth(revenue.previous_through)}`;
  }
  return `vs. ${formatCurrency(revenue.previous)} el mes anterior`;
};

export const Home = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const revenueTooltip = useOnboardingTooltip('revenue_tooltip_dismissed');
  const navigate = useNavigate();

  // The clinic's month, not the browser's — they differ for anyone working from another zone.
  const clinicCurrentMonth = dashboardData
    ? currentMonthIn(dashboardData.timezone)
    : getCurrentMonth();
  const isCurrentMonth = selectedMonth === clinicCurrentMonth;

  const handleRevenueCardClick = () => {
    revenueTooltip.dismiss();
    navigate('/reports');
  };

  // Load dashboard data
  useEffect(() => {
    const loadDashboard = async () => {
      setLoadingDashboard(true);
      setDashboardError(null);
      try {
        const data = await getDashboardData(selectedMonth);
        setDashboardData(data);
      } catch (err) {
        console.error('Error loading dashboard:', err);
        setDashboardError(err instanceof Error ? err.message : 'Error al cargar el dashboard');
      } finally {
        setLoadingDashboard(false);
      }
    };

    loadDashboard();
  }, [selectedMonth]);

  // Search patients with debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery.trim().length > 0) {
        handleSearch();
      } else {
        setSearchResults([]);
        setShowResults(false);
        setSearchError(null);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleSearch = async () => {
    if (searchQuery.trim().length === 0) return;

    setIsSearching(true);
    setSearchError(null);
    try {
      const results = await searchPatients(searchQuery, 10);
      setSearchResults(results);
      setShowResults(true);
    } catch (error) {
      console.error('Error searching patients:', error);
      setSearchError(error instanceof Error ? error.message : 'Error al buscar pacientes');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return 'Sin visitas';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-GT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="flex min-h-screen w-full flex-col p-6 bg-[#f6f7f8]">
      <Navbar showLogout={true} />

      <main className="flex flex-1 flex-col items-center justify-start py-6">
        <div className="w-full max-w-6xl">
          {/* Personalized greeting */}
          <DashboardGreeting
            firstName={dashboardData?.user_first_name ?? ''}
            data={dashboardData}
          />

          {/* Search Bar */}
          <div className="relative w-full mb-8">
            <div className="relative">
              <svg
                className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                className="w-full rounded-xl border-0 bg-white py-4 pl-14 pr-4 text-base sm:text-xl shadow-[0_4px_12px_0_rgba(0,0,0,0.05)] placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:ring-offset-2"
                placeholder="Buscar Paciente..."
                style={{ height: '64px' }}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 200)}
              />
            </div>

            {searchError && (
              <div className="absolute top-full z-10 mt-2 w-full rounded-xl bg-red-50 border border-red-200 p-4 shadow-lg">
                <p className="text-sm text-red-600">{searchError}</p>
              </div>
            )}

            {showResults && !searchError && (
              <div className="absolute top-full z-10 mt-2 w-full rounded-xl bg-white p-2 shadow-lg">
                {searchResults.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.map((patient) => (
                      <div
                        key={patient.id}
                        className="flex cursor-pointer items-center justify-between rounded-xl p-4 hover:bg-[#f6f7f8]"
                        onClick={() => {
                          setShowResults(false);
                          setSearchQuery('');
                          navigate(`/history/${patient.id}`, {
                            state: { patientName: patient.full_name },
                          });
                        }}
                      >
                        <div>
                          <p className="font-bold text-[#101822]">{patient.full_name}</p>
                          <p className="text-sm text-slate-500">Tel: {patient.phone}</p>
                        </div>
                        <p className="text-sm text-slate-500">
                          Última Visita: {formatDateShort(patient.last_visit_date)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  !isSearching && (
                    <div className="rounded-xl bg-white p-4">
                      <p className="text-sm text-slate-500 text-center">No se encontraron pacientes</p>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          {/* Dashboard Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-[#101822]">Resumen</h2>
            <MonthSelector
              selectedMonth={selectedMonth}
              onChange={setSelectedMonth}
              anchorMonth={clinicCurrentMonth}
            />
          </div>

          {/* Dashboard Content */}
          {loadingDashboard ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : dashboardError ? (
            <div className="rounded-xl bg-red-50 border border-red-200 p-6 text-center">
              <p className="text-red-600">{dashboardError}</p>
              <button
                onClick={() => setSelectedMonth(getCurrentMonth())}
                className="mt-4 text-sm text-primary hover:underline"
              >
                Reintentar
              </button>
            </div>
          ) : dashboardData ? (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {/* Full width on mobile: the currency value is the longest string in the grid and
                    the subtitle needs the room. The other two share the row below it. */}
                <div className="relative col-span-2 lg:col-span-1">
                  <StatCard
                    title="Ingresos del Mes"
                    value={formatCurrency(dashboardData.monthly_revenue.current)}
                    trend={
                      dashboardData.monthly_revenue.has_comparison
                        ? dashboardData.monthly_revenue.change_percentage
                        : undefined
                    }
                    subtitle={buildRevenueSubtitle(dashboardData.monthly_revenue)}
                    icon="payments"
                    color="green"
                    onClick={handleRevenueCardClick}
                    showChevron
                    className="h-full"
                  />
                  <OnboardingTooltip
                    visible={revenueTooltip.visible}
                    onDismiss={revenueTooltip.dismiss}
                    message="Haz clic aquí para ver tu reporte detallado de ingresos."
                  />
                </div>
                <StatCard
                  title="Total Pacientes"
                  value={dashboardData.patients.total}
                  subtitle={`+${dashboardData.patients.new_this_month} ${isCurrentMonth ? 'este mes' : 'en el mes'}`}
                  icon="group"
                  color="blue"
                />
                {/* Lead with the figure that follows the month selector; today's count is only
                    meaningful while looking at the current month. */}
                <StatCard
                  title="Visitas del Mes"
                  value={dashboardData.completed_visits_month}
                  subtitle={isCurrentMonth ? `${dashboardData.visits_today} hoy` : undefined}
                  icon="event"
                  color="purple"
                />
              </div>

              {/* Activity and Pending Payments */}
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3">
                  <RecentActivityList activities={dashboardData.recent_activity} />
                </div>
                <div className="lg:col-span-2">
                  <PendingPaymentTable
                    payments={dashboardData.pending_payment_list}
                    summary={dashboardData.pending_payments}
                  />
                </div>
              </div>
            </>
          ) : null}
        </div>
      </main>

      <Footer />
    </div>
  );
};
